/**
 * モバイル用ボトムシート (2026-07-29)。
 *
 * スマホでフィルタ群のような「一時的に開いて操作して閉じる」UI に使う。
 * ダイアログ (中央モーダル) より親指の届く位置に出るため、片手操作に向く。
 *
 * 実装はごく素朴に固定配置 + オーバーレイ。開いている間は背面のスクロールを止める。
 */
import { X } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';

export function BottomSheet({
  open,
  onClose,
  title,
  footer,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  footer?: ReactNode;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[210] bg-black/45 animate-in fade-in"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-label={title}
        className="fixed inset-x-0 bottom-0 z-[220] rounded-t-2xl border-t border-[var(--border)] bg-[var(--bg-surface)] shadow-2xl flex flex-col max-h-[85dvh]"
      >
        {/* ドラッグハンドル風の目印（実際のドラッグは未対応） */}
        <div className="pt-2 pb-1 flex justify-center flex-shrink-0">
          <span className="h-1 w-10 rounded-full bg-[var(--border)]" />
        </div>
        <div className="flex items-center justify-between px-4 pb-2 flex-shrink-0">
          <h3 className="text-[15px] font-semibold">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="閉じる"
            className="p-1.5 rounded-md text-[var(--text-muted)] hover:bg-[var(--bg-hover)]"
          >
            <X className="size-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-3">{children}</div>
        {footer && (
          <div className="flex-shrink-0 border-t border-[var(--border)] px-4 py-3 pb-[calc(12px+env(safe-area-inset-bottom,0px))]">
            {footer}
          </div>
        )}
      </div>
    </>
  );
}
